/**
 * 认证 API（扫码登录/会话/权限/退出）。
 *
 * 收敛为单一 `authApi` 对象导出，内部实现函数不顶层导出；调用方经 `authApi.method(...)` 调用。
 * 对齐后端 identity 模块 REST 契约（管理端账号密码登录已由 admin-wechat-qr-login 下线）：
 * - POST /auth/wechat-login/qrcode      -> WechatLoginQrcode
 * - GET  /auth/wechat-login/sessions/:t -> WechatLoginSession
 * - GET  /auth/wechat-login/mock-staffs -> { items: MockLoginStaff[] }
 * - POST /auth/wechat-login/mock-login  -> LoginResult
 * - GET  /auth/session                  -> SessionInfo
 * - GET  /auth/permissions              -> PermissionSnapshot
 * - POST /auth/logout                   -> void
 *
 * 路径从 `api.config.ts` 导入，禁止内联；Transport 统一解包 `{success, data}` 信封。
 */
import { ApiPaths } from './api.config';
import { http } from './transport';
import type { StaffCampusGrant } from './staff.api';

/** 扫码二维码展示通道（IKBFM0 三态，对齐后端 WechatQrcodeMode）。 */
export type WechatQrcodeMode = 'embed' | 'qrcode' | 'mock';

/**
 * 判定扫码二维码展示通道：mode 字段优先，缺失时按 qrcodeUrl 前缀推断
 * （mock:// 前缀 → mock 点选；qrconnect → embed 内嵌 iframe；其余真实 URL → qrcode 二维码图）。
 */
export function resolveWechatQrcodeMode(
  qrcodeUrl: string,
  mode?: WechatQrcodeMode,
): WechatQrcodeMode {
  if (mode) return mode;
  if (qrcodeUrl.startsWith('mock://')) return 'mock';
  if (qrcodeUrl.includes('/connect/qrconnect')) return 'embed';
  return 'qrcode';
}

/**
 * 构造微信官方扫码页 iframe 嵌入地址（embed 通道，登录页与员工绑定弹窗共用）：
 * 追加 jssdk 登录形态、黑色样式、自定义样式表（public/wechat-login.css）与 self_redirect。
 *
 * - self_redirect=true：手机确认后由 iframe 自身跳转回调地址，而非默认的 window.top 整页跳转。
 *   Firefox 禁止跨域 iframe 在无用户交互时导航顶级窗口（防点击劫持），微信页的整页跳转会被
 *   拦截（DOMException: The operation is insecure）导致回调永不发生、登录卡死；iframe 内
 *   跳转后回调页与顶层同源，可安全引导顶层窗口。
 * - 参数必须经 searchParams 写入——后端下发的 qrcodeUrl 尾部带 #wechat_redirect 锚点，
 *   字符串拼接会把参数丢进 fragment，微信收不到 login_type/style/href（2026-08-28 踩坑，
 *   表现为注入样式表从未生效 + 落到默认新模板撑出滚动条；elephant-class 线上正确姿势同此）。
 */
export function buildWechatEmbedUrl(qrcodeUrl: string): string {
  try {
    const url = new URL(qrcodeUrl);
    const origin = typeof window === 'undefined' ? '' : window.location.origin;
    url.searchParams.set('login_type', 'jssdk');
    url.searchParams.set('self_redirect', 'true');
    url.searchParams.set('style', 'black');
    // v 查询串防注入样式表被缓存（改样式必须同步 bump）
    if (origin) url.searchParams.set('href', `${origin}/wechat-login.css?v=5`);
    return url.toString();
  } catch {
    return qrcodeUrl;
  }
}

/** 扫码登录二维码（对齐后端 WechatLoginQrcodeResponseDto）。 */
export interface WechatLoginQrcode {
  /** 一次性场景令牌（OAuth2 state，5 分钟时效） */
  sceneToken: string;
  /** 扫码授权地址（embed=qrconnect，qrcode=服务号网页授权 URL，fake=mock:// 占位） */
  qrcodeUrl: string;
  /** 展示通道（后端 IKBFM0 起新增；旧实例缺失时前端按 qrcodeUrl 前缀推断） */
  mode?: WechatQrcodeMode;
  /** 过期时间 */
  expiresAt: string;
}

/** 扫码登录会话状态（对齐后端 WechatLoginSessionResponseDto）。 */
export interface WechatLoginSession {
  /** pending | confirmed | unregistered | expired | failed */
  status: 'pending' | 'confirmed' | 'unregistered' | 'expired' | 'failed';
  /** 失败原因码（仅 failed） */
  errorCode?: string;
  /** 登录载荷（仅 confirmed） */
  login?: LoginResult;
}

/** fake 模式可选员工（精简字段，对齐后端 MockLoginStaffDto）。 */
export interface MockLoginStaff {
  /** 员工 ID */
  id: string;
  /** 员工编号 */
  staffNo?: string | null;
  /** 姓名 */
  name: string;
}

/** 登录响应（对齐后端 LoginResponseDto，微信身份登录统一载荷）。 */
export interface LoginResult {
  /** 会话令牌（同时作为 Transport Authorization Bearer） */
  token: string;
  /** 员工 ID */
  staffId: string;
  /** 姓名 */
  name: string;
  /** 当前机构 ID（取员工首个有效授权校区的所属机构） */
  organizationId: string;
  /** 授权校区列表 */
  campusGrants?: StaffCampusGrant[];
}

/** 会话信息（对齐后端 SessionInfoDto）。 */
export interface SessionInfo {
  /** 员工 ID */
  staffId: string;
  /** 姓名 */
  name: string;
  /** 岗位列表 */
  positions: string[];
  /** 权限列表 */
  permissions?: unknown[];
  /** 授权校区列表 */
  campusGrants?: StaffCampusGrant[];
  /** 当前校区 ID */
  currentCampusId?: string;
}

/**
 * 权限快照（对齐后端 GET /auth/permissions 响应）。
 *
 * `menuPermissions` 是服务端动作权限的权威快照；`menuKeys` 仅保留给既有菜单展示兼容。
 */
export interface PermissionSnapshot {
  /** 员工 ID */
  staffId: string;
  /** 岗位列表 */
  positions: string[];
  /** 校区范围（逗号分隔的校区 ID，空字符串表示无授权） */
  campusScope: string;
  /**
   * 可访问菜单键列表（如 ['ADM-25','ADM-26']）。
   * 仅用于菜单可见性兼容，不用于动作按钮判定。
   */
  menuKeys?: string[];
  /** 服务端返回的菜单动作权限。 */
  menuPermissions?: Array<{
    menuKey: string;
    actions: string[];
    dataScope?: string;
  }>;
  /** 是否持全机构数据范围（任一权限 dataScope=ALL_ORG） */
  hasAllOrgScope?: boolean;
  /** 授权校区 ID 列表 */
  grantedCampusIds?: string[];
}

/** 仅依据服务端动作快照判定菜单权限，禁止由岗位推断权限。 */
export function hasMenuAction(
  permissions: PermissionSnapshot | null | undefined,
  menuKey: string,
  action: string,
): boolean {
  return permissions?.menuPermissions?.some(
    (permission) => permission.menuKey === menuKey && permission.actions.includes(action),
  ) ?? false;
}

/**
 * 从权限快照解析可查看（VIEW）菜单键列表。
 *
 * 路由守卫与菜单精确过滤共用口径：menuPermissions 权威快照在身时仅保留含
 * VIEW 动作的键（仅有 EDIT 等写动作的键不放行页面）；旧实例缺失
 * menuPermissions 时退回 menuKeys 兼容。快照缺失（未加载/拉取失败）返回
 * undefined，调用方按岗位兜底。
 */
export function resolveViewMenuKeys(
  permissions: PermissionSnapshot | null | undefined,
): string[] | undefined {
  if (!permissions) return undefined;
  if (permissions.menuPermissions) {
    return permissions.menuPermissions
      .filter((permission) => permission.actions.includes('VIEW'))
      .map((permission) => permission.menuKey);
  }
  return permissions.menuKeys;
}

/** 获取扫码登录二维码（identity-access:SC-65）。 */
async function getLoginQrcode(): Promise<WechatLoginQrcode> {
  return http.post<WechatLoginQrcode>(ApiPaths.auth.wechatLoginQrcode);
}

/** 轮询扫码登录会话状态（identity-access:SC-66~SC-69，登录页 2s 间隔调用）。 */
async function pollLoginSession(token: string): Promise<WechatLoginSession> {
  return http.get<WechatLoginSession>(ApiPaths.auth.wechatLoginSession(token));
}

/** fake 模式可选员工列表（identity-access:SC-70，real 模式 404）。 */
async function listMockLoginStaffs(): Promise<{ items: MockLoginStaff[] }> {
  return http.get<{ items: MockLoginStaff[] }>(ApiPaths.auth.wechatLoginMockStaffs);
}

/** fake 模式员工点选登录（identity-access:SC-70，real 模式 404）。 */
async function mockLogin(staffId: string): Promise<LoginResult> {
  return http.post<LoginResult>(ApiPaths.auth.wechatLoginMockLogin, { staffId });
}

async function getSession(): Promise<SessionInfo> {
  return http.get<SessionInfo>(ApiPaths.auth.session);
}

async function getPermissions(): Promise<PermissionSnapshot> {
  return http.get<PermissionSnapshot>(ApiPaths.auth.permissions);
}

async function logout(): Promise<void> {
  await http.post<void>(ApiPaths.auth.logout);
}

/** 认证 API 唯一对外入口 */
export const authApi = {
  getLoginQrcode,
  pollLoginSession,
  listMockLoginStaffs,
  mockLogin,
  getSession,
  getPermissions,
  logout,
};
